import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'

import { EmptyArray, sum } from 'lib/array/arrayUtils'

/**
 * Tracks which opt-in bundles the customer has ticked on a single rate tile.
 *
 * Selection is deliberately local (not Redux): nothing outside the tile's own Add-to-Cart/Book-Now
 * flow reads it, and the price it contributes is applied at display time only — never written back
 * into `rate.totals`, which every other consumer (margin, GTM, agent commission, price details)
 * reads directly.
 */
export function usePropertyOptionalBundleSelection(
  optionalBundles: Array<App.Property.OptionalBundle>,
  rateId: string,
) {
  const [checkedBundleIds, setCheckedBundleIds] = useState<Set<string>>(() => new Set())

  // Switching rate re-renders this component tree rather than remounting it, so the previous rate's
  // selection would otherwise stick around against bundles that no longer exist.
  useEffect(() => {
    setCheckedBundleIds(previousCheckedBundleIds => previousCheckedBundleIds.size ? new Set() : previousCheckedBundleIds)
  }, [rateId])

  const onToggleBundle = useCallback((bundleId: string, event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked

    setCheckedBundleIds((previousCheckedBundleIds) => {
      const nextCheckedBundleIds = new Set(previousCheckedBundleIds)
      if (checked) {
        nextCheckedBundleIds.add(bundleId)
      } else {
        nextCheckedBundleIds.delete(bundleId)
      }
      return nextCheckedBundleIds
    })
  }, [])

  const tickedBundles = useMemo<Array<App.Property.OptionalBundle>>(() => {
    if (!checkedBundleIds.size) return EmptyArray
    return optionalBundles.filter(optionalBundle => checkedBundleIds.has(optionalBundle.bundleId))
  }, [checkedBundleIds, optionalBundles])

  const tickedBundlesTotalPrice = useMemo(
    () => sum(tickedBundles, optionalBundle => optionalBundle.totalPrice),
    [tickedBundles],
  )

  return {
    checkedBundleIds,
    tickedBundles,
    tickedBundlesTotalPrice,
    onToggleBundle,
  }
}

export default usePropertyOptionalBundleSelection
